import { INote, INoteMutation, INotePhoto, INotesResponse } from "@eventr/common";

/** A photo taken on this device that has not been uploaded yet. Its image is in the photo store. */
export interface ILocalNotePhoto {
  localId: string;
}

export type TNotePhoto = INotePhoto | ILocalNotePhoto;

export const isLocalPhoto = (photo: TNotePhoto): photo is ILocalNotePhoto => "localId" in photo;

/** A queued change, whose photos may still be waiting to upload. */
export interface INoteChange extends Omit<INoteMutation, "photos"> {
  photos: TNotePhoto[];
}

/** A note as the app shows it: the server's copy with any queued change laid over it. */
export interface IAppNote extends Omit<INote, "photos"> {
  photos: TNotePhoto[];
  /** A change to it has not reached the server yet. */
  unsynced?: boolean;
}

export interface INoteAuthor {
  id: number;
  name: string;
}

/**
 * Lays queued changes over a list of notes, newest `modifiedAt` winning per note. Timestamps
 * are ISO 8601 in UTC, which sorts as text. A delete removes the note.
 */
export const applyNoteChanges = (
  notes: IAppNote[],
  changes: INoteChange[],
  itineraryId: number,
  author: INoteAuthor,
): IAppNote[] => {
  const byId = new Map(notes.map((note) => [note.id, note]));
  const appliedAt = new Map<string, string>();

  for (const change of changes) {
    const existing = byId.get(change.id);
    const seen = appliedAt.get(change.id) ?? existing?.modifiedAt;
    if (seen !== undefined && seen > change.modifiedAt) continue;
    appliedAt.set(change.id, change.modifiedAt);

    if (change.deleted) {
      byId.delete(change.id);
      continue;
    }

    const wasPublished = existing?.status === "published" && existing.publishedAt;
    byId.set(change.id, {
      id: change.id,
      itineraryId,
      entryId: change.entryId,
      sectionId: change.sectionId,
      title: change.title,
      body: change.body,
      status: change.status,
      authorId: existing?.authorId ?? author.id,
      authorName: existing?.authorName ?? author.name,
      createdAt: existing?.createdAt ?? change.modifiedAt,
      modifiedAt: change.modifiedAt,
      publishedAt: change.status === "published" ? (wasPublished ? existing.publishedAt : change.modifiedAt) : null,
      photos: change.photos,
      unsynced: true,
    });
  }

  return [...byId.values()];
};

/**
 * The server's notes with changes still queued laid over them. A note the server reports
 * deleted stays deleted, whatever is queued for it.
 */
export const reconcileNotes = (
  server: INotesResponse,
  queued: INoteChange[],
  itineraryId: number,
  author: INoteAuthor,
): IAppNote[] => {
  const deleted = new Set(server.deletedIds);
  return applyNoteChanges(
    server.notes.filter((note) => !deleted.has(note.id)),
    queued.filter((change) => !deleted.has(change.id)),
    itineraryId,
    author,
  );
};

/** The change that saves a note's current state, stamped now. */
export const noteToChange = (
  note: Pick<IAppNote, "id" | "entryId" | "sectionId" | "title" | "body" | "status" | "photos">,
  modifiedAt = new Date().toISOString(),
): INoteChange => ({
  id: note.id,
  entryId: note.entryId,
  sectionId: note.sectionId,
  title: note.title,
  body: note.body,
  status: note.status,
  photos: note.photos,
  modifiedAt,
});

export const countOwnDrafts = (notes: IAppNote[], userId: number | undefined): number =>
  userId === undefined ? 0 : notes.filter((note) => note.authorId === userId && note.status === "draft").length;

/** What a reader other than the author may see: published notes only. */
export const visibleNotes = (notes: IAppNote[], userId: number | undefined): IAppNote[] =>
  notes.filter((note) => note.authorId === userId || note.status === "published");
